import React, { useEffect, useState } from 'react';
import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Divider, Input, Row, Space, Spin } from 'antd';
import List from 'app/components/List';
import Modal from 'app/components/Modal';
import Tag from 'app/components/Tag';
import styled from '@emotion/styled';
import { useLazyGetProductsQuery } from 'app/features/products/api';
import ProductItem from '../ProductItem';
import { IProduct } from 'app/types/interfaces/Product';
import debounce from 'lodash/debounce';
import { DataType, StaticContext } from 'app/providers/StaticProvider';
import Checkbox from 'app/components/Checkbox';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

const StyledModal = styled(Modal)`
  .ant-modal-footer {
    text-align: left;
    p {
      font-size: 12px;
      font-weight: 500;
      line-height: 16px;
      letter-spacing: 0.005em;
      text-align: left;
      color: rgba(0, 0, 0, 1);
      margin-bottom: 0;
    }
  }
  .ant-list-item {
    padding-right: 16px;
  }
`;

const ModalSelectProduct = ({
  open,
  selectedValue,
  onCancel,
  onOk
}: {
  open: boolean;
  selectedValue: Array<any>;
  onCancel: () => void;
  onOk: (value: any[]) => void;
}) => {
  const { getOptions } = React.useContext(StaticContext);
  const [fetchProduct, result] = useLazyGetProductsQuery();
  const [keywords, setKeywords] = React.useState<string>('');
  const [data, setData] = React.useState<Array<IProduct>>([]);
  const params = React.useRef<any>({
    pageSize: 20,
    current: 1,
    keywords: '',
    categories: []
  });

  const previousValue = React.useRef<any[]>([]);
  const [value, setValue] = React.useState<any[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);

  React.useEffect(() => {
    setValue(selectedValue);
    previousValue.current = selectedValue;
  }, [selectedValue]);

  useEffect(() => {
    if (data && value.length) {
      const isContainEvery = data.every(
        (selectedItem) => !!value.find((x) => x.id === selectedItem.id)
      );

      setIsSelectAll(isContainEvery);
    } else {
      setIsSelectAll(false);
    }
  }, [value, data]);

  const loadMoreData = async () => {
    if (result.isLoading) return;
    params.current = {
      ...params.current,
      current: params.current.current + 1
    };
    const nextParams = {
      ...params.current
    };

    if (!nextParams.keywords) {
      delete (params as any).keywords;
    }
    try {
      const response = await fetchProduct(nextParams).unwrap();
      setData(data.concat(response.data));
    } catch (error) {
      console.error(ModalSelectProduct.name, error);
    }
  };

  const fetch = async () => {
    const nextParams = {
      ...params.current
    };
    if (!nextParams.keywords) {
      delete (nextParams as any).keywords;
    }
    try {
      const response = await fetchProduct(nextParams as any).unwrap();
      setData(response.data);
    } catch (error) {
      console.error(ModalSelectProduct.name, error);
    }
  };

  const onSearch = debounce((value) => {
    params.current = {
      ...params.current,
      keywords: value,
      current: 1
    };
    fetch();
  }, 250);

  // const onChangeOption = debounce((value: any) => {
  //   params.current = {
  //     ...params.current,
  //     categories: value === 'all_category' ? [] : [value],
  //     current: 1
  //   };
  //   fetch();
  // }, 250);

  React.useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSelectAll = async (e: CheckboxChangeEvent) => {
    const nextParams = {
      ...params.current,
      current: 1,
      pageSize: 99999
    };
    if (!nextParams.keywords) {
      delete (nextParams as any).keywords;
    }
    const res = await fetchProduct({
      ...nextParams
    }).unwrap();

    if (e.target.checked) {
      const tempSelected = [...value];

      res.data.forEach((item) => {
        if (!value.find((x) => x.id === item.id)) {
          tempSelected.push(item);
        }
      });

      setValue(tempSelected);
    } else {
      setValue(
        value.filter(
          (selectedItem) => !res.data.some((x) => x.id === selectedItem.id)
        )
      );
    }

    setIsSelectAll(e.target.checked);
  };

  return (
    <StyledModal
      width={700}
      maskClosable={false}
      open={open}
      title={'Select Product(s)'}
      cancelButtonProps={{ hidden: true }}
      footer={
        <Space align="center" size={20}>
          <Button
            type="primary"
            onClick={() => {
              onOk(value);
            }}
          >
            Confirm
          </Button>
        </Space>
      }
      onCancel={() => {
        setValue(previousValue.current);
        onCancel();
      }}
      // inputProps={{
      //   placeholder: 'Search by product name, item no. , customer tag',
      //   onChange: (event) => {
      //     onSearch(event.target.value);
      //   }
      // }}
      // selectProps={{
      //   onChange: onChangeOption,
      //   defaultValue: 'all_category',
      //   options: [
      //     {
      //       value: 'all_category',
      //       label: 'All categories'
      //     }
      //   ].concat(getOptions(DataType.CATEGORIES))
      // }}
    >
      <Input.Search
        allowClear
        prefix={<SearchOutlined />}
        value={keywords}
        placeholder="Search by product name, item no. , customer tag"
        enterButton="Search"
        onChange={(e) => {
          setKeywords(e.target.value);
        }}
        onSearch={(keyword) => {
          onSearch(keyword);
        }}
      ></Input.Search>
      <Divider />
      <Row
        justify="space-between"
        wrap
        style={{ marginBottom: 8, paddingRight: 16 }}
      >
        <Checkbox
          disabled={result.isLoading || result.isFetching}
          checked={isSelectAll}
          onChange={handleChangeSelectAll}
        >
          Select All
        </Checkbox>
        <Tag color={value.length > 0 ? '#005EFF' : '#F0F5FF'}>
          <div style={{ color: value.length > 0 ? '#FFF' : '#4E5A71' }}>
            Selected {value.length} product{value.length > 1 && 's'}
          </div>
        </Tag>
      </Row>
      <List
        id="lazyProductList"
        isLazy={true}
        dataSource={data}
        height={500}
        hasMore={true}
        loadMoreData={loadMoreData}
        listProps={{
          loading: result.isLoading || result.isFetching
        }}
        renderItem={(item, index) => {
          const isSelected = value.some((selected) => selected.id === item.id);
          return (
            <ProductItem
              buttonProps={{
                type: 'primary',
                danger: false,
                ghost: !isSelected,
                children: isSelected ? 'Selected' : 'Select',
                icon: isSelected ? <CheckOutlined /> : null,
                onClick: () => {
                  if (isSelected) {
                    setValue(
                      value.filter((selected) => selected.id !== item.id)
                    );
                  } else {
                    setValue(value.concat(item));
                  }
                }
              }}
              product={item}
              order={index}
            ></ProductItem>
          );
        }}
      ></List>
    </StyledModal>
  );
};

export default ModalSelectProduct;
